import React, { useEffect, useState } from 'react';
import { MagicXData, OtpErrorResponse, ParsedPromo } from '../../components/Layout/Promotions/types';
import { UserTags } from '../useFetchPlatformPromos/types';
import { checkIsContentOnlyPromo, getMagicXData, isActivePromotion } from '../../components/Layout/Promotions/utils';
import { checkIsUrlExternal } from '../../utils/links-helper/links-helper';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useSignIn } from '../auth';
import { useSnackbar } from '../snackbar';
import { useTranslations } from '../useTranslationsHelper';
import { getAvailablegMxPromotions, optInMxPromotion } from '../../components/Layout/Promotions/services';
import { ERROR_STR } from '../../consts';
import { setLoadPlatformPromotions } from '../../state/promotions/reducers';
import { useDispatch } from 'react-redux';
import { useUserContext } from '../../providers/UserProvider';
import { storageClaimedPromo } from '../../utils/storage';

type HandleClaimClick = (promo?: ParsedPromo) => void;

type UsePromotionClaimProps = {
  setHandleClaimClick: React.Dispatch<React.SetStateAction<HandleClaimClick>>;
  userTags?: Nullable<UserTags>;
};

export const usePromoClaim = ({ setHandleClaimClick }: UsePromotionClaimProps): void => {
  const { t } = useTranslations();
  const { isAuthenticated } = useAuth();
  const { addSnack } = useSnackbar();
  const { user } = useUserContext();

  const signIn = useSignIn();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [startClaim, setStartClaim] = useState<boolean>(false);
  const [claimedPromo, setClaimedPromo] = useState<ParsedPromo | null>(null);
  const [storedPromo, setStoredPromo] = useState<ParsedPromo | null>(null);

  const isActivePromotions = isActivePromotion(claimedPromo?.status);

  const getPlatormMagicXCTA = async (claimedPromoStorage: ParsedPromo): Promise<MagicXData> => {
    const receivedAvailableMXPromotions = await getAvailablegMxPromotions(user?.email);
    const matchedPromo = receivedAvailableMXPromotions.find((promo) => promo?.slug === claimedPromoStorage.magicXSlug);

    return getMagicXData(matchedPromo);
  };

  const onPromotionClaimed = (): void => {
    addSnack({
      theme: 'dark',
      type: 'success',
      message: t('promotions.claimed'),
    });
  };

  const onClaimedError = (): void => {
    addSnack({
      theme: 'dark',
      type: 'error',
      message: t('error.common'),
      isDismissible: false,
    });
  };

  const onActivateBonus = async (): Promise<void> => {
    const { magicXSlug, magicXCta } = claimedPromo || {};

    try {
      const optResponse = await optInMxPromotion(magicXSlug, magicXCta);

      if ((optResponse as OtpErrorResponse)?.name === ERROR_STR) {
        onClaimedError();
      } else {
        onPromotionClaimed();
      }

      dispatch(setLoadPlatformPromotions(true));
    } catch (error) {
      onClaimedError();
    } finally {
      setStartClaim(false);
    }
  };

  const goToCtaLink = (): void => {
    const ctaUrl = String(claimedPromo?.ctaUrl);

    if (checkIsUrlExternal(ctaUrl)) {
      window.location.href = String(ctaUrl);
    } else {
      navigate(`/${ctaUrl}`);
    }
  };

  const claimHandler: HandleClaimClick = (promo?: ParsedPromo): void => {
    promo && setClaimedPromo(promo);
    setStartClaim(true);
  };

  useEffect(() => {
    setHandleClaimClick(() => claimHandler);
  }, []);

  useEffect(() => {
    if (startClaim) {
      const isContentOnlyPromo = checkIsContentOnlyPromo(claimedPromo);
      const shouldGoToCtaLink = isContentOnlyPromo || isActivePromotions;

      if (isAuthenticated) {
        shouldGoToCtaLink ? goToCtaLink() : onActivateBonus();
      } else {
        storageClaimedPromo.set({ value: JSON.stringify(claimedPromo) });
        signIn({ redirectUri: window.location.href });
      }
    }
  }, [startClaim]);

  useEffect(() => {
    if (storedPromo) {
      setClaimedPromo(storedPromo);
      setStartClaim(true);
    }
  }, [storedPromo]);

  // Extract previously opted promotion, compare it with platform and set to claim
  useEffect(() => {
    if (user?.email) {
      (async (): Promise<void> => {
        const claimedPromoStorage: ParsedPromo = JSON.parse(String(storageClaimedPromo.get()));

        if (claimedPromoStorage) {
          const { magicXCta, magicXAvailabilityTagName, magicXActivityTagName } =
            await getPlatormMagicXCTA(claimedPromoStorage);
          const resultStoredPromo = {
            ...claimedPromoStorage,
            magicXCta,
            magicXAvailabilityTagName,
            magicXActivityTagName,
          };

          setStoredPromo(resultStoredPromo);
        }

        storageClaimedPromo.remove();
      })();
    }
  }, [user?.email]);
};
